'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useConversation } from '@elevenlabs/react';
import { Button } from '@components/common/Forms/Button/Button';
import { useTranslation } from 'next-i18next';
import { cn } from '@root/src/utils/cn';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { ApiGetPhoneNumber, fetcher, isUserGetPhoneNumberOk } from '@utils/data-fetching';
import { PulsatingBestieAvatar } from '@components/templates/audio-interview/partials/PulsatingBestieAvatar';

type ElevenLabsConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected';

interface ElevenLabsInterviewRoomContentProps {
  locale: Locale;
  conversationToken: string;
  onDisconnect: () => void;
}

/**
 * ElevenLabsInterviewRoomContent
 *
 * Content component that renders the ElevenLabs audio interview UI.
 * Uses the ElevenLabs useConversation hook to manage the conversation.
 *
 * Props:
 * - conversationToken: The token to connect to the ElevenLabs agent
 * - onDisconnect: Callback when disconnecting from the interview
 */
const ElevenLabsInterviewRoomContent: React.FC<ElevenLabsInterviewRoomContentProps> = ({
  locale,
  conversationToken,
  onDisconnect,
}) => {
  const { t } = useTranslation('common');
  const [connectionState, setConnectionState] = useState<ElevenLabsConnectionState>('idle');
  const [hasStarted, setHasStarted] = useState(false);
  const [loadingIdentity, setLoadingIdentity] = useState(true);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const headerInfo = useAppSelector(getUserHeaderInfo);
  const [dynamicVariables, setDynamicVariables] = useState<Record<string, string> | null>(null);

  const conversation = useConversation({
    serverLocation: 'eu-residency',
    onConnect: () => {
      setConnectionState('connected');
    },
    onDisconnect: () => {
      setConnectionState('disconnected');
    },
    onError: (error: string) => {
      console.error('ElevenLabs conversation error:', error);
      setConnectionState('disconnected');
    },
  });

  // prepare dynamicVariables: always send phone_number / user_slug when available
  useEffect(() => {
    let cancelled = false;

    const resolveIdentity = async () => {
      try {
        // Try to get user`s phone number
        const phoneRes = await fetcher<ApiGetPhoneNumber>(
          '/v1/user/phone-number',
          locale,
        );
        let phoneNumber: string | null = null;

        if (isUserGetPhoneNumberOk(phoneRes)) {
          phoneNumber = `+${phoneRes.data.country.data.countryCode}${phoneRes.data.number}`;
        }

        if (cancelled) return;

        const userSlug = headerInfo?.userSlug ?? null;
        const vars: Record<string, string> = {};
        if (userSlug) vars.user_slug = userSlug;
        if (phoneNumber) vars.phone_number = phoneNumber;

        if (Object.keys(vars).length > 0) {
          setDynamicVariables(vars);
          setIdentityError(null);
          setLoadingIdentity(false);
          return;
        }

        // Neither phone nor user_slug available
        setDynamicVariables(null);
        setIdentityError(t('global.something.went.wrong'));
        setLoadingIdentity(false);
      } catch (e) {
        console.error('Failed to resolve ElevenLabs identity variables', e);
        if (!cancelled) {
          setDynamicVariables(null);
          setIdentityError(t('global.something.went.wrong'));
          setLoadingIdentity(false);
        }
      }
    };

    void resolveIdentity();

    return () => {
      cancelled = true;
    };
  }, [locale, headerInfo?.userSlug, t]);

  // Start conversation after identity is resolved
  const startConversation = useCallback(async () => {
    if (hasStarted) return;
    if (loadingIdentity) return;
    if (!dynamicVariables) {
      // identity missing: do not start
      return;
    }
    setHasStarted(true);
    setConnectionState('connecting');

    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Start the conversation using the private WebRTC session (conversationToken only)
      // For EU residency, the token was obtained from the EU API endpoint
      await conversation.startSession({
        conversationToken,
        // Provide user identity to the agent
        dynamicVariables,
      });
    } catch (error) {
      console.error('Failed to start ElevenLabs conversation:', error);
      setConnectionState('disconnected');
    }
  }, [conversation, conversationToken, hasStarted, dynamicVariables, loadingIdentity]);

  useEffect(() => {
    void startConversation();
  }, [startConversation]);

  // Handle disconnect
  const handleDisconnect = useCallback(async () => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('Error ending ElevenLabs session:', error);
    }
    onDisconnect();
  }, [conversation, onDisconnect]);

  let connectionStatusText = '';
  if (connectionState === 'connecting') {
    connectionStatusText = t('audio-interview.connecting');
  } else if (connectionState === 'connected') {
    connectionStatusText = t('audio-interview.connected');
  }

  // Loading identity
  if (loadingIdentity && !identityError) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <span className="text-sm text-ink-light">
          {t('global.event.loading')}
          ...
        </span>
      </div>
    );
  }

  // Identity error
  if (!loadingIdentity && (identityError || !dynamicVariables)) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <p className="text-center text-sm text-gray-500">{t('global.something.went.wrong')}</p>
        <Button
          type="button"
          styling="outline"
          color="error"
          size="md"
          rounding="normal"
          onClick={onDisconnect}
          className="min-w-32"
        >
          {t('global.action.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-[600px] flex-col">
      {/* Header */}
      <div className="mb-8 text-center">
        <h2 className="mb-2 text-2xl font-bold text-ink">
          {t('audio-interview.title')}
        </h2>
        <div className="flex items-center justify-center space-x-2">
          <div className={cn('size-2 rounded-full', connectionState === 'connected' ? 'bg-secondary' : 'bg-ink-light')} />
          <span className="text-sm text-ink-light">
            {connectionStatusText}
          </span>
        </div>
      </div>

      {/* Main Content - Centered Bestie */}
      <div className="flex flex-1 flex-col items-center justify-center">
        <PulsatingBestieAvatar
          isConnected={connectionState === 'connected'}
        />
      </div>

      {/* Footer Controls */}
      <div className="mt-12 flex flex-col items-center space-y-4">
        <div className="flex items-center space-x-4">
          <Button
            type="button"
            styling="outline"
            color="error"
            size="md"
            rounding="normal"
            onClick={() => { void handleDisconnect(); }}
            className="min-w-32"
          >
            {t('audio-interview.exit-interview')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ElevenLabsInterviewRoomContent;
